import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Number of rows to show in the skeleton
   * @default 5
   */
  rows: p.number(5),

  /**
   * Show toolbar skeleton (search, filters, actions)
   * @default true
   */
  showToolbar: p.boolean(true),

  /**
   * Show pagination skeleton
   * @default true
   */
  showPagination: p.boolean(true),

  /**
   * Number of columns to show
   * @default 4
   */
  columns: p.number(4),
} satisfies ComponentPropsOptions;

export type TableSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * TableSkeleton - Loading placeholder for DataTable components
 *
 * Provides a consistent skeleton UI for tables with toolbar, rows, and pagination.
 * Designed to match the DataTable component structure.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <TableSkeleton />
 *
 * // Custom rows
 * <TableSkeleton rows={10} />
 *
 * // Without toolbar
 * <TableSkeleton showToolbar={false} />
 *
 * // With QueryLoader
 * <QueryLoader queries={[myQuery]}>
 *   <QueryLoader.Loading>
 *     <TableSkeleton rows={3} />
 *   </QueryLoader.Loading>
 *   <DataTable.Root table={table}>
 *     ...
 *   </DataTable.Root>
 * </QueryLoader>
 * ```
 */
const TableSkeleton = defineComponent({
  name: "TableSkeleton",
  props,
  setup(props) {
    return () => (
      <div class="w-full p-2">
        {/* Toolbar skeleton */}
        {props.showToolbar && (
          <div class="flex items-center justify-between gap-2 pb-4">
            {/* Search/filter area */}
            <div class="flex flex-1 items-center gap-2">
              <Skeleton class="h-8 w-64 rounded-md" />
              <Skeleton class="h-8 w-32 rounded-md" />
            </div>
            {/* Action buttons */}
            <div class="flex items-center gap-2">
              <Skeleton class="h-8 w-24 rounded-md" />
              <Skeleton class="h-8 w-32 rounded-md" />
            </div>
          </div>
        )}

        {/* Table skeleton */}
        <div class="rounded-md border-t">
          {/* Header row */}
          <div class="flex h-12 items-center border-b">
            {Array.from({ length: props.columns }).map((_, i) => (
              <Skeleton
                key={`header-${i}`}
                class="mr-4 h-4 flex-1 rounded"
                style={{ maxWidth: i === 0 ? "50%" : "100%" }}
              />
            ))}
          </div>

          {/* Data rows */}
          {Array.from({ length: props.rows }).map((_, rowIndex) => (
            <div key={`row-${rowIndex}`} class="flex items-center border-b py-4 last:border-b-0">
              {Array.from({ length: props.columns }).map((_, colIndex) => (
                <Skeleton
                  key={`cell-${rowIndex}-${colIndex}`}
                  class="mr-4 h-4 flex-1 rounded"
                  style={{
                    maxWidth: colIndex === 0 ? "50%" : colIndex === 1 ? "30%" : "100%",
                  }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Pagination skeleton */}
        {props.showPagination && (
          <div class="flex items-center justify-between border-t pb-2 pt-4">
            <Skeleton class="h-6 w-48 rounded" />
            <div class="flex items-center gap-2">
              <Skeleton class="h-6 w-6 rounded-md" />
              <Skeleton class="h-6 w-6 rounded-md" />
              <Skeleton class="h-6 w-6 rounded-md" />
              <Skeleton class="h-6 w-6 rounded-md" />
            </div>
          </div>
        )}
      </div>
    );
  },
});

export default TableSkeleton;
